import { responsive, useTheme } from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useMemo } from 'react';
import { getContainerStyles } from '@/utils';

export const useStyles = (isDark: boolean) => {
  const { theme } = useTheme();

  return useMemo(
    () => ({
      footer: css`
        width: 100%;
        background: var(--common-color);
        border-top: 1px solid rgba(255, 255, 255, 0.1);
      `,

      container: css`
        ${getContainerStyles(theme)};
        ${responsive.create({
          paddingTop: {
            default: theme.spacing.get(56),
            md: theme.spacing.get(60),
            lg: theme.spacing.get(64),
          },
          paddingBottom: {
            default: theme.spacing.get(52),
            md: theme.spacing.get(60),
            lg: theme.spacing.get(64),
          },
        })}
      `,

      footerText: css`
        font-weight: 400;
        line-height: 1.6;
        color: var(--desc-text-color);
        ${responsive.create({
          fontSize: { default: '14px', md: '14px', lg: '16px' },
        })}
      `,

      socialMediaContainer: css`
        ${responsive.create({ gap: { default: '12px', md: '10px', lg: '12px' } })}
      `,

      socialMediaWrapper: css`
        padding: 8px;
        background-color: var(--social-media-color);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        ${responsive.create({
          width: { default: '36px', md: '32px', lg: '42px' },
          height: { default: '36px', md: '32px', lg: '42px' },
        })}
      `,

      footerHeading: css`
        font-weight: 600;
        color: var(--text-color);
        ${responsive.create({
          fontSize: { default: '20px', md: '18px', lg: '20px' },
        })}
      `,

      servicesList: css`
        display: flex;
        flex-direction: column;
        list-style: none;
        padding: 0;
        margin: 0;
        gap: 12px;
      `,

      contactIconBase: css`
        color: ${isDark ? '#17ceca' : '#4054D1'};
      `,

      contactIconLeft: css`
        margin-right: 12px;
      `,

      textFieldContainer: css`
        position: relative;
        width: 100%;
        max-width: 100%;
        overflow: hidden;
      `,

      textFieldButtonWrapper: css`
        position: absolute;
        right: 0;
        top: -38px;
        bottom: 0;
        display: flex;
        align-items: center;
        z-index: 2;
        ${responsive.create({
          width: { default: '46px', md: '48px', lg: '52px' },
        })}
      `,

      textFieldButton: css`
        min-width: auto;
        padding: 0;
        border-radius: 50%;

        ${responsive.create({
          height: { default: '46px', md: '48px', lg: '52px' },
          width: { default: '46px', md: '48px', lg: '52px' },
          marginTop: { default: '2px' },
        })}
      `,

      newsLetterTextFieldContainer: css`
        position: relative;
        width: 100%;
        max-width: 100%;
        --tf-filled-bg-color: var(--tf-bg-muted-color);
        --tf-bg-color: var(--tf-bg-muted-color);
        --tf-active-color: var(--tf-text-color);
        --tf-border-color: var(--tf-border-color);
        color: var(--tf-text-color);

        input {
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &:focus-within {
          --tf-border-color: var(--tf-active-color);
        }
      `,

      copyrightContainer: css`
        text-align: center;
        ${responsive.create({
          marginTop: {
            default: '72px',
            md: '70px',
            lg: '100px',
          },
        })}
      `,

      copyrightCompanyName: css`
        font-weight: 600;
        font-size: 0.95rem;
        color: ${
          isDark
            ? theme.colors.brandColors.secondary?.default
            : theme.colors.brandColors.primary?.default
        };
        margin-left: 4px;
      `,
    }),
    [isDark, theme],
  );
};
